import React, { useMemo, useState } from "react";
import {
  Breadcrumb,
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Divider,
  Tooltip,
  Row,
  Col,
  Alert,
} from "antd";
import { Link } from "react-router-dom";

import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";

const { Search, TextArea } = Input;

const MOCK_INDENT_REQUESTS = [
  {
    key: "ind-1",
    indentId: "#IND-123",
    location: "Wing D, Flat 1001",
    item: "Tap",
    qty: 4,
    requestor: "Contractor A",
    date: "2024-01-15",
    status: "PENDING_APPROVAL",
  },
  {
    key: "ind-2",
    indentId: "#IND-124",
    location: "Wing E, Flat 2005",
    item: "Mixer",
    qty: 2,
    requestor: "Contractor B",
    date: "2024-01-16",
    status: "APPROVED",
  },
  {
    key: "ind-3",
    indentId: "#IND-125",
    location: "Wing F, Flat 3002",
    item: "Washbasin",
    qty: 1,
    requestor: "Contractor A",
    date: "2024-01-17",
    status: "PENDING_APPROVAL",
  },
];

const WING_OPTIONS = [
  { label: "Wing A", value: "A" },
  { label: "Wing B", value: "B" },
  { label: "Wing C", value: "C" },
  { label: "Wing D", value: "D" },
];

const FLAT_OPTIONS = [
  { label: "1001", value: "1001" },
  { label: "1002", value: "1002" },
  { label: "1003", value: "1003" },
  { label: "2001", value: "2001" },
  { label: "2002", value: "2002" },
];

const IndentRequests = () => {
  const [createForm] = Form.useForm();
  const [approvalForm] = Form.useForm();

  const [indentList, setIndentList] = useState(MOCK_INDENT_REQUESTS);
  const [searchValue, setSearchValue] = useState("");
  const [isCreateIndentModalOpen, setIsCreateIndentModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedIndent, setSelectedIndent] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [selectedWing, setSelectedWing] = useState(null);
  const [selectedFlat, setSelectedFlat] = useState(null);
  const [issueQty, setIssueQty] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const filteredData = useMemo(() => {
    return indentList.filter((req) => {
      const matchesSearch =
        !searchValue ||
        req.indentId.toLowerCase().includes(searchValue.toLowerCase()) ||
        req.location.toLowerCase().includes(searchValue.toLowerCase()) ||
        req.item.toLowerCase().includes(searchValue.toLowerCase());
      return matchesSearch;
    });
  }, [searchValue, indentList]);

  const handleOpenApprovalModal = (record) => {
    setSelectedIndent(record);
    setIssueQty(record.qty);
    approvalForm.setFieldsValue({
      issueQty: record.qty,
      rejectionReason: "",
    });
    setIsApprovalModalOpen(true);
  };

  const handleApprove = async () => {
    const values = await approvalForm.validateFields();
    if (values.issueQty <= 0) {
      return;
    }
    // eslint-disable-next-line no-console
    console.log("Approve & Issue", {
      indent: selectedIndent,
      issueQty: values.issueQty,
    });

    // Update status in local state
    setIndentList((prev) =>
      prev.map((item) =>
        item.key === selectedIndent.key ? { ...item, status: "APPROVED" } : item
      )
    );

    setIsApprovalModalOpen(false);
    setSelectedIndent(null);
    setIssueQty(null);
    setRejectionReason("");
    approvalForm.resetFields();
  };

  const handleReject = async () => {
    const values = approvalForm.getFieldsValue();
    // eslint-disable-next-line no-console
    console.log("Reject Request", {
      indent: selectedIndent,
      rejectionReason: values.rejectionReason || "",
    });

    // Update status in local state
    setIndentList((prev) =>
      prev.map((item) =>
        item.key === selectedIndent.key ? { ...item, status: "REJECTED" } : item
      )
    );

    setIsApprovalModalOpen(false);
    setSelectedIndent(null);
    setIssueQty(null);
    setRejectionReason("");
    approvalForm.resetFields();
  };

  const handleCreateIndent = async () => {
    const values = await createForm.validateFields();
    // eslint-disable-next-line no-console
    console.log("Submit Indent Request", values);

    // Add new indent to list
    const newIndent = {
      key: `ind-${indentList.length + 1}`,
      indentId: `#IND-${String(indentList.length + 126).padStart(3, "0")}`,
      location: `Wing ${selectedWing}, Flat ${selectedFlat}`,
      item: values.itemName || "Tap",
      qty: values.qty || 0,
      requestor: "Contractor A",
      date: new Date().toISOString().split("T")[0],
      status: "PENDING_APPROVAL",
    };

    setIndentList((prev) => [...prev, newIndent]);
    setIsCreateIndentModalOpen(false);
    createForm.resetFields();
    setSelectedWing(null);
    setSelectedFlat(null);
    setItemSearch("");
  };

  const columns = [
    { title: "Indent ID", dataIndex: "indentId", key: "indentId", width: 120 },
    { title: "Location", dataIndex: "location", key: "location", width: 180 },
    { title: "Item", dataIndex: "item", key: "item", width: 150 },
    { title: "Qty", dataIndex: "qty", key: "qty", width: 80, align: "right" },
    { title: "Requestor (Contractor)", dataIndex: "requestor", key: "requestor", width: 180 },
    { title: "Date", dataIndex: "date", key: "date", width: 120 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (status) => {
        if (status === "APPROVED") return <Tag color="green">{status}</Tag>;
        if (status === "PENDING_APPROVAL") return <Tag color="orange">{status}</Tag>;
        if (status === "REJECTED") return <Tag color="red">{status}</Tag>;
        return <Tag>{status}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => {
        if (record.status === "PENDING_APPROVAL") {
          return (
            <Button
              type="primary"
              size="small"
              className="SaveGradiantBtn"
              onClick={() => handleOpenApprovalModal(record)}
            >
              Approve / Reject
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="View and create contractor indent requests">
              <h3>Contractor Indent Requests</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Material Indent</Breadcrumb.Item>
              <Breadcrumb.Item>Indent Requests</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <Button
              type="primary"
              className="SaveGradiantBtn"
              onClick={() => setIsCreateIndentModalOpen(true)}
            >
              <span className="material-symbols-outlined">
add
</span> New Indent Request 
            </Button>
          </div>
        </div>
      </div>

      <section className="grid-sec tablegridsec">
        <div className="PR-body">
          <div className="po-good-header" style={{justifyContent:'flex-end'}}>
            <Space wrap size={10}>
              <div className="searchwrp">
                <Search
                  placeholder="Search by Indent ID, Location, or Item..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  allowClear
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  style={{ width: 320 }}
                />
              </div>
              <Button size="small" type="primary" className="SaveGradiantBtn">Filter</Button>
            </Space>
          </div>

          <div className="tablewrp">
            <Table
              rowKey="key"
              columns={columns}
              dataSource={filteredData}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `Showing ${range[0]}–${range[1]} of ${total} requests`,
              }}
              scroll={{ x: 1100 }}
            />
          </div>
        </div>
      </section>

      {/* Modal 1: New Indent Request */}
      <Modal
        title="New Contractor Indent Request"
        open={isCreateIndentModalOpen}
        onCancel={() => {
          setIsCreateIndentModalOpen(false);
          createForm.resetFields();
          setSelectedWing(null);
          setSelectedFlat(null);
          setItemSearch("");
        }}
        footer={null}
        width={600}
      >
        <Form layout="vertical" form={createForm}>
        <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Select Destination</div>
                </Divider>
         
                <Row gutter={[15]}>
                <Col  xs={24} sm={12} > 
            <Form.Item
              label="Wing"
              name="wing"
              rules={[{ required: true, message: "Wing is required" }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Select
                placeholder="Select Wing"
                options={WING_OPTIONS}
                value={selectedWing}
                onChange={setSelectedWing}
              />
            </Form.Item>
            </Col> 
            <Col  xs={24} sm={12} > 
            <Form.Item
              label="Flat"
              name="flat"
              rules={[{ required: true, message: "Flat is required" }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Select
                placeholder="Select Flat"
                options={FLAT_OPTIONS}
                value={selectedFlat}
                onChange={setSelectedFlat}
              />
            </Form.Item>
            </Col>
          </Row>
       
          <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Search Item</div>
                </Divider>
          
          <Space wrap size={10} style={{ width: "100%" }}>
            <Form.Item name="itemSearch" style={{ flex: 1, minWidth: 300 }}>
              <Search
                placeholder="e.g., Tap"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                allowClear
                prefix={
                  <img
                    src={SearchIcon}
                    alt="SearchIcon"
                    className="SearchIcon"
                    width={22}
                  />
                }
              />
            </Form.Item>
            
          </Space>
          <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Smart Info (Read-Only Panel)</div>
                </Divider>
                <div className="po-good-header">
                  <div className="po-header-details" >
                    <div className="po-row"><strong>Already Issued to Flat {selectedFlat || "1001"}: </strong><span>3 Nos</span></div>
                    <div className="po-row"><strong>Typical 2BHK Requirement: </strong><span>4 Nos</span></div>
                    </div>
                    </div>
        
                    <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Qty to Indent</div>
                </Divider>
   
          <Form.Item
            name="qty"
            rules={[{ required: true, message: "Quantity is required" }]}
          >
            <InputNumber
              min={0}
              defaultValue={0}
              style={{ width: "100px" }}
              placeholder="Enter quantity"
            />
          </Form.Item>
        </Form>

        <div className="FormFooter" style={{padding:'0px', margin:'0px'}}>
       
          <Alert
              type="warning"
              style={{
                width: "100%",
                padding: "5px 10px",
                borderRadius: 4,
              }}
              message={
                <span>
                  Status: Indent will be created as PENDING_APPROVAL upon submission.
                </span>
              }
            />
          <Space>
            <Button type="primary" className="CancelGradiantBtn"
              onClick={() => {
                setIsCreateIndentModalOpen(false);
                createForm.resetFields();
                setSelectedWing(null);
                setSelectedFlat(null);
                setItemSearch("");
              }}
            >
              Cancel
            </Button>
            <Button type="primary" className="SaveGradiantBtn" onClick={handleCreateIndent}>
              Submit Indent
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Modal 2: Store Manager Approval & Issue */}
      <Modal
        title={`Store Manager Approval & Issue (Indent ${selectedIndent?.indentId || ""})`}
        open={isApprovalModalOpen}
        onCancel={() => {
          setIsApprovalModalOpen(false);
          setSelectedIndent(null);
          setIssueQty(null);
          setRejectionReason("");
          approvalForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        {selectedIndent && (
          <>
          <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Contractor Request Details</div>
                </Divider>
            
            <Descriptions column={1} bordered size="small" style={{marginBottom:'16px'}}>
              <Descriptions.Item label="Contractor">{selectedIndent.requestor}</Descriptions.Item>
              <Descriptions.Item label="Location">{selectedIndent.location}</Descriptions.Item>
              <Descriptions.Item label="Item">{selectedIndent.item}</Descriptions.Item>
              <Descriptions.Item label="Requested Qty">{selectedIndent.qty}</Descriptions.Item>
              <Descriptions.Item label="Date">{selectedIndent.date}</Descriptions.Item>
            </Descriptions>
            <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Smart Info & Stock Check</div>
                </Divider>
                <div className="po-good-header" style={{marginBottom:'10px'}}>
                  <div className="po-header-details" >
                    <div className="po-row"><strong>Already Issued to Flat {selectedIndent.location.split("Flat ")[1] || "1001"}: </strong><span>3 Nos</span></div>
                    <div className="po-row"><strong>Typical 2BHK Requirement: </strong><span>4 Nos</span></div>
                    <div className="po-row"><strong>Current Stock Available: </strong><span>50 Nos</span></div>
                    </div>
                    </div>
            
                    <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: '5px', marginTop: '0px' }}>
                  <div className="returninfo-title">Manager Action</div>
                </Divider>
            
            <Form layout="vertical" form={approvalForm}>
              <Form.Item
                label="Issue Qty"
                name="issueQty"
                style={{width:'150px'}}
                rules={[{ required: true, message: "Issue quantity is required" }]}
                initialValue={selectedIndent.qty}
              >
                <InputNumber
                  min={0}
                  value={issueQty}
                  onChange={setIssueQty}
                  style={{ width: "100%" }}
                  placeholder="Enter issue quantity"
                />
              </Form.Item>

              <Form.Item label="Rejection Reason (Optional)" name="rejectionReason">
                <TextArea
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection reason if rejecting"
                />
              </Form.Item>
            </Form>

            <div className="FormFooter" style={{padding:'0px', margin:'0px'}}>
             
                <Button type="primary" className="rejectBtn" onClick={handleReject}>
                  Reject Request
                </Button>
                <Button type="primary" className="SaveGradiantBtn" onClick={handleApprove}>
                  Approve &amp; Issue
                </Button>
             
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default IndentRequests;
